import axios from "axios";
import {defineStore} from "pinia";
import TwilioClientObserver from "../Utilities/TwilioClientObserver";
import Utilities from "../Helpers/utilities";
import {useAppState} from "./AppState";
import {useWorkspace} from "./Workspaces";
import {useWorkspaceTree} from "./WorkspaceTree";
import Conversation from "./../Utilities/Conversation";
import {useTeam} from "../Components/Custom/Team/Store/Team";
import {useUserStore} from "./User";
import DateUtilities from "../Utilities/DateUtilities";
import {useInvitation} from "./Invitation";
import {useRequest} from "../Components/Chat/Store/Request.js";
import HttpStatusCodes from "../Constants/http.js";
import {useTeamPanel} from "Admin/js/Stores/TeamPanel.js";
import {useAlignStore} from "./Align.js";
import {handleInviteApi} from "../Helpers/chin";
import {useChat} from "../Components/Chat/Store/Chat";
import {useAssociateStore} from "./Associate";
import {useAuthorityLevelStore} from "./AuthorityLevel";
import {useUnlinkedPanelState} from "./useUnlinkedPanelState";
import {MEET_YOUR_KAAMFU_AI} from "../Constants/menus";
import {
    buildWorkPanelInviteProfilesPayload,
    filterWorkPanelInviteProfileIds,
} from "../Components/Custom/Team/Composables/workPanelMembershipInvitePayload";
import { applyConversationEntityUpdate } from "../Helpers/updateConversationReference.js";
import { shouldDismissLocateForConversationType } from "../Helpers/activeLocateHighlight";
import { isTaskStartOrResumeSubType } from "../Helpers/taskTimerActivityQueue.js";
import {
    getMessageScrollPriority,
    mergeUnreadIndicator,
    MESSAGE_SCROLL_RESERVATION_TTL_MS,
} from "../Helpers/unreadMessages.js";

/**
 * @param {import("../Utilities/Conversation").default | { id?: number | null, type?: string | null, type_id?: number | null, key?: number, state?: { id?: number | null, type?: string | null, type_id?: number | null } } | null | undefined} conv
 */
export function getPendingMediaScopeKey(conv) {
    if (!conv) {
        return "";
    }
    const id = conv.id ?? conv.state?.id;
    if (id) {
        return `c:${id}`;
    }
    const type = conv.type ?? conv.state?.type;
    const typeId = conv.type_id ?? conv.state?.type_id;
    if (type != null && typeId != null) {
        return `d:${type}:${typeId}`;
    }
    const key = conv.key;
    if (key != null) {
        return `k:${key}`;
    }
    return "";
}

export const useConversation = defineStore("Conversation", {
    state: () => ({
        is_chat_panel_opened: false,
        list: [],
        handlers: [],
        activeIndex: 0,
        activeHistoryId: null,
        hasConversationAccess: false,
        conversation_history: [],
        history: [],
        history_index: -1,
        is_conversation: false,
        dm_profiles_in_progress: [],
        is_twilio_disconnected: false,
        is_end_assignment: false,
        is_invitation_in_progress: false,
        unread_conversations: [],
        /**
         * Unread line state per conversation id:
         * `{ last_read_message_index, unread_count, separator_after_index }`.
         */
        unread_indicators: {},
        message_scroll_reservation: null,
        flagged_conversations: [],
        conversations_sids_in_loading: new Map(),
        conversations_ids_in_loading: new Map(),
        image_src_array: [],
        is_twilio_reconnecting: false,
        is_initialized: false,
        group_conversations: [],
        loading: false,
        ignore_watch_on_set_message_tab: false,
        title: null,
        is_copy_and_move_open: false,
        non_org_conversations_ids: [],
        non_org_conversation_aq: new Map(),
        hide_conversation_details: false,
        buffered_restore_conversation: [],
        is_annotation_images_loading: false,
        initial_loading: false,
        isArchivingOrRestoring: false,
        is_get_help_now_opened: false,
        pending_uploaded_media_ids_by_scope: {},
        pending_membership_fake_item_id: null,
        pending_membership_support_toggle: false,
    }),
    getters: {
        active: (state) => state.handlers[state.activeIndex],
        is_trying_to_connect: (state) => state.is_twilio_reconnecting,
        is_not_trying_to_connect: (state) => !state.is_twilio_reconnecting,
        has_twilio_disconnected: (state) => state.is_twilio_disconnected,
        groups_open: (state) =>
            state.group_conversations?.filter((group) => !group.is_archived),
        groups_archived: (state) =>
            state.group_conversations?.filter((group) => group.is_archived),
        groups: (state) => [...state.groups_open, ...state.groups_archived],
        is_creating_conversation: (state) =>
            Boolean(state.active?.state?.isCreatingConversation),
        temporary_title: (state) =>
            state.loading && state.title ? state.title : null,
        sorted_unread_conversations: (state) => {
            if (!Array.isArray(state.unread_conversations)) {
                return [];
            }

            // Oldest queued first (FIFO). Do not use lastMessageDateCreated —
            // that is conversation activity, not when the thread entered AQ.
            return [...state.unread_conversations]
                .map((item, index) => ({item, index}))
                .sort((a, b) => {
                    const a_queued = a.item?.queued_at ?? 0;
                    const b_queued = b.item?.queued_at ?? 0;
                    if (a_queued !== b_queued) {
                        return a_queued - b_queued;
                    }

                    return a.index - b.index;
                })
                .map(({item}) => item);
        },
        isActiveQuickConversation: (state) => {
            const conversation = state.active;
            return (
                conversation &&
                !conversation?.hasTwilioConversationSet() &&
                !conversation?.getMessages()?.length &&
                !conversation?.is_dm &&
                !conversation?.is_group &&
                !conversation?.is_topic &&
                !conversation?.is_group_one_off
            );
        },
    },
    actions: {
        syncItemInWorkspaceTree(partialItem) {
            const workspaceTree = useWorkspaceTree();
            if (!partialItem?.id) {
                return;
            }
            const existing = workspaceTree.getItemById(partialItem.id);
            if (!existing) {
                return;
            }
            const patch = {};
            if (partialItem.item_category_id != null) {
                patch.item_category_id = partialItem.item_category_id;
                patch.itemCategoryId = partialItem.item_category_id;
            }
            if (
                partialItem.version != null &&
                typeof partialItem.version === "object"
            ) {
                patch.version = {
                    ...(existing.version ?? {}),
                    ...partialItem.version,
                };
            }
            if (Object.keys(patch).length > 0) {
                workspaceTree.syncItemById(partialItem.id, patch);
            }
        },

        /**
         * After a successful move into a destination item category, mirror
         * automatic_close / automatic_end_assignment on workspace items and on
         * item conversations' originalData (same rules as WorkspaceTree.moveItems).
         * Call only after the reorder API succeeds or after live-sync tree updates.
         *
         * @param {number[]} movedItemIds
         * @param {{ automatic_close?: boolean, automatic_end_assignment?: boolean }} itemCategory
         */
        applyDestinationItemCategoryMoveSideEffects(
            movedItemIds,
            itemCategory,
        ) {
            const workspaceTree = useWorkspaceTree();
            const invitation = useInvitation();

            movedItemIds.forEach((itemId) => {
                const item = workspaceTree.getItemById(itemId);
                if (!item) {
                    return;
                }

                this.updateSpaceAndBoardByItem(item);

                const patch = {};

                if (itemCategory.automatic_close) {
                    patch.status = "closed";
                } else if (item.status === "closed") {
                    patch.status = "open";
                    if (
                        item.version?.current_assignment_status ===
                        "end_assigned_by_completion"
                    ) {
                        patch.version = {
                            ...item.version,
                            current_assignment_status: null,
                        };
                    }
                }

                if (itemCategory.automatic_end_assignment && item.version) {
                    patch.version = {
                        ...(patch.version ?? item.version),
                        current_assignment_status: "end_assigned_by_completion",
                    };
                }

                if (
                    item.version &&
                    (itemCategory.automatic_end_assignment ||
                        itemCategory.automatic_close)
                ) {
                    patch.version = {
                        ...(patch.version ?? item.version),
                        last_assignee_id: item.version.user_profile_id_assignee,
                    };
                }

                if (Object.keys(patch).length > 0) {
                    workspaceTree.syncItemById(itemId, patch);
                }
            });

            const conversations = this.getConversationsByItemIds(movedItemIds);
            conversations.forEach((conv) => {
                if (
                    conv.type !== invitation.ACCESS_LEVELS.ITEM ||
                    !conv?.originalData?.item
                ) {
                    return;
                }

                if (itemCategory.automatic_close) {
                    conv.originalData.item.status = "closed";
                } else if (conv.originalData.item.status === "closed") {
                    conv.originalData.item.status = "open";
                    if (
                        conv.originalData.item.version
                            ?.current_assignment_status ===
                        "end_assigned_by_completion"
                    ) {
                        conv.originalData.item.version.current_assignment_status =
                            null;
                    }
                }

                if (
                    itemCategory.automatic_end_assignment &&
                    conv.originalData.item.version
                ) {
                    conv.originalData.item.version.current_assignment_status =
                        "end_assigned_by_completion";
                }

                if (
                    conv.originalData.item.version &&
                    (itemCategory.automatic_end_assignment ||
                        itemCategory.automatic_close)
                ) {
                    conv.originalData.item.version.last_assignee_id =
                        conv.originalData.item.version.user_profile_id_assignee;
                }

                if (this.active?.id === conv.id) {
                    this.active.originalData.item = conv.originalData.item;
                }
            });
        },

        addPendingUploadedMediaId(mediaId, conversation) {
            const scope = getPendingMediaScopeKey(conversation);
            if (!mediaId || !scope) {
                return;
            }
            const existing =
                this.pending_uploaded_media_ids_by_scope[scope] ?? [];
            if (existing.includes(mediaId)) {
                return;
            }
            this.pending_uploaded_media_ids_by_scope = {
                ...this.pending_uploaded_media_ids_by_scope,
                [scope]: [...existing, mediaId],
            };
        },
        getPendingUploadedMediaIdsForConversation(conversation) {
            const scope = getPendingMediaScopeKey(conversation);
            if (!scope) {
                return [];
            }
            return [...(this.pending_uploaded_media_ids_by_scope[scope] ?? [])];
        },
        clearPendingUploadedMediaIdsForConversation(conversation, idsSent) {
            const scope = getPendingMediaScopeKey(conversation);
            if (
                !scope ||
                !(scope in this.pending_uploaded_media_ids_by_scope)
            ) {
                return;
            }
            const current =
                this.pending_uploaded_media_ids_by_scope[scope] ?? [];
            const sentSet =
                Array.isArray(idsSent) && idsSent.length > 0
                    ? new Set(idsSent)
                    : null;
            const remaining =
                sentSet === null
                    ? []
                    : current.filter((id) => !sentSet.has(id));
            const next = {...this.pending_uploaded_media_ids_by_scope};
            if (remaining.length === 0) {
                delete next[scope];
            } else {
                next[scope] = remaining;
            }
            this.pending_uploaded_media_ids_by_scope = next;
        },
        removeConversationsFromHistory(conversation_ids) {
            if (Array.isArray(conversation_ids)) {
                conversation_ids.forEach((id) => {
                    const index = this.history.findIndex(
                        (conv) => conv.id === id,
                    );
                    if (index !== -1) {
                        this.history.splice(index, 1);
                    }
                });
            }
        },

        async updateGroupConversationName(id, payload) {
            const res = await axios.patch("conversations/" + id, payload);
            return res.data?.data;
        },
        async grabImageURLsFromServer() {
            try {
                this.is_annotation_images_loading = true;
                const res = await axios.get(
                    `/medias/conversations/${this.active.id}?index=0&batch_size=30&media_type=image`,
                );
                return res.data?.data?.map(({path}) => path)?.reverse();
            } catch {
                return [];
            } finally {
                this.is_annotation_images_loading = false;
            }
        },

        disconnected() {
            if (this.is_twilio_disconnected) {
                return;
            }

            this.is_twilio_disconnected = true;
            this.is_twilio_reconnecting = true;

            setTimeout(() => {
                this.is_twilio_reconnecting = false;
            }, 60000);
        },
        connected() {
            this.is_twilio_disconnected = false;
        },
        twilioConnectingStarted() {
            this.twilio_connection_started_at = Date.now();
        },
        resetTwilioConnectingStarted() {
            this.twilio_connection_started_at = 0;
        },
        /* async initialize(list) {
             this.list = list
             this.prepareConversations()
         },*/

        prepareConversations() {
            this.list?.forEach((convData, index) => {
                const convHandler = new Conversation(convData, false);
                this.handlers.push(convHandler);
            });

            this.handlers.forEach((conversation) => {
                if (!conversation.is_quick_load) conversation.initialize();
            });
        },
        loadSpaceBoardsAndItemsForConversations() {
            const item_ids = [];
            this.handlers
                // .slice(0, 99)
                .forEach(
                    (conv) => conv.isItem() && item_ids.push(conv.type_id),
                );

            useWorkspace()
                .loadSpaceAndBoardsForItemIds(item_ids)
                .then(() => this.setWorkSpaceBoardDetailsForEachConversation());
        },
        setWorkSpaceBoardDetailsForEachConversation() {
            this.handlers
                .filter((conv) => conv.isItem())
                .forEach((conv) => conv.attachItemsSpaceAndBoard());
        },
        restore(id, is_opened) {
            this.openConversationById(id);

            // if (is_opened == null)
            //     return

            // Utilities()?.toggleChatPopover(is_opened)
        },

        restoreRecentlyOpenedConversations(conversation_histories) {
            this.history = conversation_histories.reverse();
            this.history_index = this.history.length - 1;
        },
        findIndexById(conv_id) {
            return this.handlers?.findIndex((conv) => conv.id == conv_id);
        },
        findById(conv_id) {
            return this.handlers?.find((conv) => conv.id == conv_id);
        },
        findByTemporaryId(temporary_id) {
            return this.handlers?.find(
                (conv) => conv.temporary_id == temporary_id,
            );
        },
        findGroupById(conv_id) {
            return this.group_conversations?.find((conv) => conv.id == conv_id);
        },
        findBySId(sid) {
            return this.handlers?.find((conv) => conv.sid == sid);
        },
        getForItemId(item_id) {
            const mappedConvIndex = this.handlers.findIndex(
                (conv) => conv.isItem() && conv.resourceId == item_id,
            );
            return mappedConvIndex >= 0 ? this.handlers[mappedConvIndex] : null;
        },
        updateGroupNameById(conversation) {
            const conversation_object = this.findGroupById(conversation.id);
            if (!conversation_object)
                return console.log("No Conversation found to update name");

            conversation_object.name = conversation.name;
            conversation_object.avatar_url = conversation.avatar_url;
        },
        updateNameByItem(item) {
            if (item?.id == null) {
                return;
            }

            const conv = this.handlers.find(
                (c) => c.isItem() && c.resourceId == item.id,
            );
            if (!conv) {
                return console.log("No Conversation found to update name");
            }

            const name = item.name;
            conv.name = name;
            if (conv.originalData?.item?.id === item.id) {
                conv.originalData.item.name = name;
            }
        },
        removeConversationById(conversation_id) {
            const index = this.handlers.findIndex(
                (conv) => conv.id === conversation_id,
            );
            if (index !== -1) {
                this.handlers.splice(index, 1);
            }
        },
        updateNameByType(item) {
            if (!item) return;
            const conv = this.handlers.find(
                (conv) =>
                    (conv.type === item.type ||
                        conv.type === item?.entity_type) &&
                    conv.resourceId == item.id,
            );
            if (!conv)
                return console.log("No Conversation found to update name");

            conv.name = item.name;
        },
        /**
         * Sync open conversation names / parent labels after a LiveSync entity rename.
         * Matches handlers via originalData.conversationable_type + conversationable_id
         * (falling back to type / type_id).
         */
        updateConversationReference(entity, conversationable_type) {
            applyConversationEntityUpdate(
                this.handlers,
                entity,
                conversationable_type,
            );
        },
        updateAssigneeByItem(item_id, assignee_id = null) {
            const conv = this.handlers.find(
                (conv) => conv.isItem() && conv.resourceId == item_id,
            );
            if (!conv)
                return console.log("No Conversation found to update assignee");

            conv.setAssignee(assignee_id);

            if (conv.originalData?.item?.version) {
                conv.originalData.item.version.user_profile_id_assignee =
                    assignee_id;
            }
        },
        updateOriginalDataByItem(item) {
            const conv = this.handlers.find(
                (conv) => conv.isItem() && conv.resourceId == item.id,
            );
            if (!conv)
                return console.log("No Conversation found to update assignee");

            conv.originalData.item = item;
            conv.setAssignee(item?.version?.user_profile_id_assignee);
        },
        updateSpaceAndBoardByItem(item, new_board = null, new_space = null) {
            if (!item?.id) {
                return;
            }
            const conv =
                this.handlers.find((c) => c.isItem() && item.id == c.type_id) ??
                (this.active?.isItem() && item.id == this.active.type_id
                    ? this.active
                    : null);
            if (!conv) {
                return console.log(
                    "No Conversation found to update space and Board",
                );
            }

            if (new_space && new_board) {
                conv.updateSpaceAndBoard(new_space, new_board);
                conv.updateKey();
                return;
            }

            const workspaceTree = useWorkspaceTree();
            const resolvedItem = workspaceTree.getItemById(item.id);
            const board = resolvedItem
                ? workspaceTree.getBoardById(
                    resolvedItem.boardId ?? resolvedItem.board_id,
                )
                : null;
            const space = resolvedItem
                ? workspaceTree.getWorkspaceById(
                    resolvedItem.workspaceId ?? resolvedItem.workspace_id,
                )
                : null;
            conv.updateSpaceAndBoard(space, board);
            //            conv.updateKey();
        },

        setSpaceName(space_name) {
            return (this.state.space_name = space_name);
        },
        updateCurrentSpaceWithNew(space) {
            const conversations = this.handlers.filter(
                (conv) =>
                    conv.space_id == space.id ||
                    (conv.isSpace() && conv.type_id == space.id),
            );

            if (!conversations)
                return console.log(
                    "No Conversation found to update board name",
                );

            conversations.forEach((conv) => (conv.space_name = space.name));
        },
        updateCurrentBoardWithNew(board) {
            const conversations = this.handlers.filter(
                (conv) =>
                    conv.board_id == board.id ||
                    (conv.isBoard() && conv.type_id == board.id),
            );

            if (!conversations)
                return console.log(
                    "No Conversation found to update board name",
                );

            conversations.forEach((conv) => {
                conv.board_name = board.name;
            });
        },

        updateWorkspaceForBoard(board, workspace) {
            const conversations = this.handlers.filter(
                (conv) =>
                    conv.board_id == board.id ||
                    (conv.isBoard() && conv.type_id == board.id),
            );
            conversations.forEach((conv) => {
                conv.space_name = workspace.name;
                conv.space_id = workspace.id;
            });
        },

        openDmConversationForUser(user_profile) {
            return this.openDmConversationForProfileId(user_profile.id);
        },
        openDmConversationForProfileId(user_profile_id) {
            const dmUserProfile = useUserStore().profiles.find(
                (profile) => profile.id == user_profile_id,
            );
            const conversation_index = this.handlers?.findIndex(
                (conv) =>
                    conv.isDm() &&
                    conv.getDMParticipant().twilio_identity ==
                    dmUserProfile.twilio_identity,
            );
            if (conversation_index != -1) {
                this.setHistoryTeamPanel();

                this.openConversastionAt(conversation_index);
                return true;
            }
            return false;
        },
        openConversationById(id) {
            return this.openOrLoadConversationById(id);
            /*if (!id) return console.log("Invalid conversation");
            const conversation_index = this.findIndexById(id);

            if (conversation_index === null || conversation_index == -1)
                return console.log(
                    `Conversation ${id} not found to restore state`,
                );

            this.toggleProductivityPanel(false);
            this.setActiveConversationAtIndex(conversation_index);*/
        },
        async createAndUpdateActiveIndexWith(conv_data, profile_id) {
            try {
                const conversation = this.findById(conv_data.id);
                if (conversation) {
                    this.hide_conversation_details = true;

                    const is_performed_by_current_user = useUserStore().profile.id === profile_id;
                    const isUnArchived = !conv_data.is_archived && conversation?.is_archived;
                    const is_loader_visible = conversation.stop_loader_on_load;
                    const isReaddedOnOpenConversation =
                        conversation.id === this.active?.id &&
                        !this.active
                            ?.getActiveParticipantIds?.()
                            ?.includes(useUserStore().getCurrentUserProfileId());

                    if ((isUnArchived || isReaddedOnOpenConversation) && !is_loader_visible) {
                        conversation.reloadExistingConversation();
                        return;
                    } else {
                        await conversation?.updateParticipants();
                        conversation?.fetchTwilioParticipants();
                    }

                    if (
                        conv_data?.type_id === this.active?.type_id &&
                        !conversation.hasTwilioConversationSet()
                    ) {
                        await conversation?.loadConversation(isUnArchived);
                    }
                    return;
                } else if (conv_data?.type_id === this.active?.type_id) {
                    if (!this.active.hasTwilioConversationSet()) {
                        this.openConversationById(conv_data.id);
                    }
                    return;
                }

                if (
                    conv_data.user_profile_id ===
                    useUserStore().getCurrentUserProfileId()
                ) {
                    return;
                }

                this.loadConversationBySID(conv_data.sid);
            } catch (error) {
                console.error(error);
            } finally {
                this.hide_conversation_details = false;
            }
        },
        async removeAndUpdateActiveIndexById(conv_id, group = false) {
            const index = this.findIndexById(conv_id);
            if (group) {
                const groupIndex = this.group_conversations.findIndex(
                    (group) => group.id === conv_id,
                );
                if (groupIndex !== -1) {
                    this.group_conversations.splice(groupIndex, 1);
                }
            }

            if (index == -1) return;

            let new_active_index = this.activeIndex;

            if (index < this.activeIndex) {
                // If we delete an item before the active one, the active item's index shifts left by 1
                new_active_index = this.activeIndex - 1;
            } else if (index === this.activeIndex) {
                // If we delete the active item itself, fallback to the previous item
                if (this.activeIndex > 0) {
                    new_active_index = this.activeIndex - 1;
                }
            //    this.loading = true;
            }
            // If index > this.activeIndex, the active item's index doesn't change

            const conversation_to_delete = this.handlers.splice(index, 1)[0];
            this.activeIndex = new_active_index;
            useChat().resetStandaloneAssociateSessionIfNeeded();

            const current_profile_id = useUserStore().getCurrentUserProfileId();
            if (conversation_to_delete && conversation_to_delete.is_item) {
                try {
                    const resource_type =
                        useInvitation().RESOURCE_TYPES[
                            conversation_to_delete.type
                            ];
                    const {data} = await axios.get(
                        `invites/${resource_type}/${conversation_to_delete.type_id}`,
                    );
                    const profile = data?.data?.user_profiles.find(
                        (user_profile) =>
                            user_profile.id === current_profile_id,
                    );
                    if (profile || profile?.has_parent_access) {
                        return;
                    }

                    const workpanelObject = {
                        type: conversation_to_delete.type,
                        id: conversation_to_delete.type_id,
                    };
                    const lineageResource = data?.data?.resource;
                    const workspaceTree = useWorkspaceTree();

                    if (workpanelObject.type === "item") {
                        const boardId = lineageResource?.board?.id;
                        const boardCategoryId =
                            workspaceTree.getBoardById(boardId)
                                ?.boardCategoryId ?? null;

                        workspaceTree.removeItemById(workpanelObject.id);

                        const itemCategoryId = lineageResource?.item_group?.id;
                        if (itemCategoryId) {
                            const itemCategory =
                                workspaceTree.getItemCategoryById(
                                    itemCategoryId,
                                );
                            if (
                                itemCategory &&
                                (!itemCategory.itemOrderedIds ||
                                    itemCategory.itemOrderedIds.length === 0)
                            ) {
                                workspaceTree.removeItemCategoryById(
                                    itemCategoryId,
                                );
                            }
                        }

                        if (boardId) {
                            const board = workspaceTree.getBoardById(boardId);
                            if (
                                board &&
                                (!board.itemCategoryOrderedIds ||
                                    board.itemCategoryOrderedIds.length === 0)
                            ) {
                                workspaceTree.removeBoardById(boardId);
                            }
                        }

                        if (boardCategoryId) {
                            const boardCategory =
                                workspaceTree.getBoardCategoryById(
                                    boardCategoryId,
                                );
                            if (
                                boardCategory &&
                                (!boardCategory.boardOrderedIds ||
                                    boardCategory.boardOrderedIds.length === 0)
                            ) {
                                workspaceTree.removeBoardCategoryById(
                                    boardCategoryId,
                                );
                            }
                        }

                        const workspaceId = lineageResource?.workspace?.id;
                        if (workspaceId) {
                            const workspace =
                                workspaceTree.getWorkspaceById(workspaceId);
                            if (
                                workspace &&
                                (!workspace.boardCategoryOrderedIds ||
                                    workspace.boardCategoryOrderedIds.length ===
                                    0)
                            ) {
                                workspaceTree.removeWorkspaceById(workspaceId);
                            }
                        }

                        const workspaceCategoryId =
                            lineageResource?.workspace_group?.id;
                        if (workspaceCategoryId && workspaceId) {
                            const workspaceCategory =
                                workspaceTree.getWorkspaceCategoryById(
                                    workspaceCategoryId,
                                );
                            if (
                                workspaceCategory &&
                                (!workspaceCategory.workspaceOrderedIds ||
                                    workspaceCategory.workspaceOrderedIds
                                        .length === 0)
                            ) {
                                workspaceTree.removeWorkspaceById(workspaceId);
                            }
                        }
                    }
                } catch (error) {
                    console.error(error);
                } finally {

                }
            }
        },
        updateState() {
            useAppState().updateConversationId(this.active.id);
        },
        openConversastionAt(index) {
            this.setActiveConversationAtIndex(index);
        },
        openPreviousConversation(prev = true) {
            if (this.conversation_history.length <= 1) return;

            const mappedConvIndex = this.conversation_history.findIndex(
                (conv) => conv.historyId === this.activeHistoryId,
            );

            const next_index = prev ? mappedConvIndex - 1 : mappedConvIndex + 1;

            if (
                next_index < 0 ||
                next_index > this.conversation_history.length - 1
            ) {
                return;
            }

            const prevConversation = this.conversation_history[next_index];

            this.openConversationForItem(
                {
                    id: prevConversation.id,
                    name: prevConversation.name,
                    historyId: prevConversation.historyId,
                },
                true,
            );
        },
        async openOrLoadItemConversation(item) {
            const item_conversation = useConversation().getForItemId(
                item.value.id,
            );
            if (item_conversation) {
                await this.openConversationForItem(item);
            } else {
                const convData = {
                    name: item.value.name,
                    type: "item",
                    type_id: item.value.id,
                    participants: ids,
                };
                this.createFakeConversastion(convData);
            }
        },
        async openConversationForItem(item) {
            this.toggleProductivityPanel(false);
            await this.openConversationOfType(
                useInvitation().ACCESS_LEVELS.ITEM,
                item,
            );
        },

        openConversationBasedOnType(data, type) {
            return this.openConversationOfType(type, data);
        },

        async openConversationOfType(
            type,
            {id, name} = data,
            with_history = true,
            conversation_id = null,
            messageToJump = null,
        ) {
            if (
                shouldDismissLocateForConversationType(
                    type,
                    useAppState().entity_navigate_with_locator,
                )
            ) {
                useWorkspace().dismissActiveLocateHighlight();
            }

            const convData = {
                id: conversation_id,
                name: name,
                type: type || useInvitation().ACCESS_LEVELS.ITEM,
                type_id: id,
            };

            let mappedConvIndex = -1;
            if (type === "group" || type === "dm") {
                mappedConvIndex = this.handlers.findIndex(
                    (conv) =>
                        conv.id == id && conv.type == type && !conv.isFake(),
                );
            } else {
                mappedConvIndex = this.handlers.findIndex(
                    (conv) =>
                        conv.resourceId == id &&
                        conv.type == type &&
                        !conv.isFake(),
                );
            }


            if (mappedConvIndex >= 0) {
                const conv = this.handlers[mappedConvIndex];
                this.setActiveConversationAtIndex(
                    mappedConvIndex,
                    with_history,
                );

                if (messageToJump) {
                    if (conv.hasTwilioConversationSet()) {
                        conv.jumpToMessage(
                            messageToJump.id,
                            messageToJump.index,
                        );
                    } else {
                        conv.setPendingMessageToJump(messageToJump);
                    }
                }

                useChat().setMessageTab();
                if (conv.isFetchedInitialMessage()) {
                    console.log("openConversationOfType stop loader immediately", convData);
                    this.stopLoader();
                } else {
                    console.log("openConversationOfType set stop loader after conversation fetch", convData);
                    conv.setStopLoaderOnLoad()
                    return;
                }

                conv?.markAsReadIfCorrectWindowOpenForMessageRead();
                return;
            }

            console.log("openConversationOfType conversation missing and fetching from backend", convData);

            try {
                this.loading = true;
                let result;
                try {
                    result = await axios.post(
                        "/conversations/verify",
                        convData,
                    );
                } catch (error) {
                    if (error.response) {
                        if (
                            error?.response?.status ===
                            HttpStatusCodes.FORBIDDEN
                        ) {
                            Utilities().showErrorSnackbar(
                                error?.response?.data?.message,
                            );
                            return;
                        } else if (
                            error?.response?.status ===
                            HttpStatusCodes.INTERNAL_SERVER_ERROR
                        ) {
                            Utilities().showErrorSnackbar(
                                "Server error occurred.",
                            );
                            return;
                        }

                        const errors = error.response.data?.errors;
                        if (errors?.archived && errors?.messages?.length) {
                            const conv_handler = new Conversation(
                                errors?.conversation,
                                true,
                            );
                            this.handlers.unshift(conv_handler);
                            conv_handler.restoreMessages(errors?.messages);
                            conv_handler.setAsArchivedConversation();
                            this.setActiveConversationAtIndex(0, with_history);
                            this.setLoader(false);
                            return;
                        }
                    }
                }

                let conversation = result?.data?.data;
                const isConversationExist = Boolean(conversation?.id);
                let convHandler;
                if (isConversationExist) {
                    convHandler = new Conversation(conversation);
                } else {
                    //remove current fake conversation if any added alredy
                    const fakeConvIndex = this.handlers.findIndex((conv) =>
                        conv.isFake(),
                    );

                    if (fakeConvIndex >= 0) {
                        this.handlers.splice(fakeConvIndex, 1);
                    }

                    convHandler = new Conversation(convData, false);
                    convHandler.setAsFakeConversation();
                }
                useChat().setMessageTab();
                convHandler.setStopLoaderOnLoad();
                this.handlers.unshift(convHandler);
                this.setActiveConversationAtIndex(0, with_history);
                convHandler.setHideLoadingIndicatorWith(true);
                this.setLoader(false, isConversationExist ? 10000 : 300);
            } finally {
            }
        },

        async sharePaymentLink(item) {
            const {data} = await axios.get(`/invoices?item_id=${item.id}`);
            if (!data || !data.id)
                return alert(
                    "not possible to share a payment link for this item",
                );

            this.active?.sendPaymentLink(data);
        },

        moveConversationToTop(conversation_id) {
            const index = this.handlers.findIndex(
                (conv) => conv.id == conversation_id,
            );

            if (index == -1)
                return console.log("invalid conversation", conversation_id);
            if (index == 0) return;

            const conversation_to_move = this.handlers.splice(index, 1)[0];
            if (!conversation_to_move) return;

            this.handlers.unshift(conversation_to_move);

            if (index == this.activeIndex)
                return this.setActiveConversationAtIndex(0, true, true, true);

            if (index > this.activeIndex)
                return this.setActiveConversationAtIndex(
                    this.activeIndex + 1,
                    true,
                    true,
                    true,
                );
        },

        isActiveConversationFake() {
            const current = this.active;
            return current?.isFake();
        },

        setActiveConversationAtIndex(
            active_index,
            with_history = true,
            set_previous_data = false,
            ignore_pre_action = false,
        ) {
            if (!ignore_pre_action) {
                this.performConversationLoadPreAction(set_previous_data);
            }
            this.activeIndex = active_index;
            this.performConversationLoadPostAction(with_history);
        },
        isUserInHistory() {
            const history_length = this.history.length;
            return (
                history_length > 1 && this.history_index < history_length - 1
            );
        },
        updateConversationHistory() {
            // if (this.isUserInHistory())
            //     this.history.splice(this.history_index + 1);
            const current_conv = this.handlers[this.activeIndex];

            if (current_conv.isFake()) return;

            this.history = this.history.filter(
                (conv) => conv.id != current_conv.id,
            );

            this.pushHistoryItem(current_conv);

            this.updateState();
        },

        performConversationLoadPostAction(with_history = true) {
            this.setTemporaryTitle();
            this.hide_conversation_details = false;

            if (with_history) {
                return this.updateConversationHistory();
            }

            this.updateState();
        },

        performConversationLoadPreAction(set_previous_data = false) {
            const leaving = this.active;
            if (leaving?.logUnread) {
                leaving.logUnread("switch-leave", {
                    leaving_conversation_id: leaving.id,
                    leaving_name: leaving.name,
                    held: leaving.hold_visible_read_after_manual_unread,
                    manual_unread_message_index:
                        leaving.manual_unread_message_index,
                    separator_after_index: leaving.unreadSeparatorIndex?.(),
                });
            }

            this.clearUnreadSeparator(this.active?.id);

            const app_state_for_workline = useAppState();
            if (app_state_for_workline.worklineFilterDrawer) {
                app_state_for_workline.worklineFilterDrawer = false;
            }
            useChat().resetStandaloneAssociateSessionIfNeeded();
            useChat().closeEmbeddedWorkerSettings();
            this.toggleGetHelpNowMenuValue(false);
            this.toggleProductivityPanel(false);
            app_state_for_workline.toggleNewDirectoryPanel(false);
            if (set_previous_data) {
                this.toggleTeamPanel(
                    false,
                    this.active,
                    useAppState()?.selected_variant_tab,
                );
                useAppState()?.toggleInviteDrawer(false, this.active);
            } else {
                const appState = useAppState();
                // Preserve variant tab (e.g. "members" when opening membership); toggleTeamPanel
                // defaults tab to "all" which would wipe members mid-flow.
                this.toggleTeamPanel(
                    false,
                    {},
                    appState?.selected_variant_tab ?? "all",
                );
                appState?.toggleInviteDrawer(false);
            }
        },

        pushHistoryItem(args) {
            this.history.push(args);
            this.history_index = this.history.length - 1;
        },

        setHistoryItemNotEqualToType(args) {
            return (this.history = this.history.filter(
                (conv) => !conv.type || conv.type != args.type,
            ));
        },

        async nextInHistory() {
            const history_length = this.history.length;
            if (this.history_index >= history_length - 1)
                return (this.history_index = history_length - 1);

            const next_conv = this.history[this.history_index + 1];

            if (!next_conv.id && next_conv.type === "team") {
                this.toggleTeamPanel();
                this.history_index += 1;
                return;
            }

            await this.openOrLoadConversationById(next_conv.id, false);
            this.history_index += 1;
        },

        async previousInHistry() {
            if (this.history_index <= 0) return (this.history_index = 0);

            const next_conv = this.history[this.history_index - 1];

            if (!next_conv.id && next_conv.type === "team") {
                this.toggleTeamPanel();
                this.history_index -= 1;
                return;
            }

            await this.openOrLoadConversationById(next_conv.id, false);
            this.history_index -= 1;
        },

        setHistoryTeamPanel() {
            const args = {
                type: "team",
                tab: "all",
            };

            this.setHistoryItemNotEqualToType(args);
            //this.pushHistoryItem(args)
        },

        toggleTeamPanel(
            val = true,
            active_conv = {},
            tab = "all",
            is_manage_organization_role = false,
        ) {
            const run = () => {
                const app_state_store = useAppState();
                app_state_store.toggleInviteDrawer(null, active_conv, val);
                app_state_store.toggleNewGroupInvite(false);
                app_state_store.setLockedSelectedVariantTab(null);
                app_state_store.setSelectedVariantTab(tab);
                app_state_store.is_manage_organization_role_opened = false;
                app_state_store.role_object = {};
                if (is_manage_organization_role) {
                    app_state_store.is_manage_organization_role_opened = true;
                }
            };
            if (val) {
                const associateStore = useAssociateStore();
                associateStore.runPendingActionAfterAssociateSaveDialogIfNeeded(
                    run,
                );
                return;
            }
            run();
        },

        toggleProductivityPanel(val = true) {
            const run = () => {
                const app_state_store = useAppState();
                app_state_store.toggleProductivityDrawer(val);
            };
            if (val) {
                const associateStore = useAssociateStore();
                associateStore.runPendingActionAfterAssociateSaveDialogIfNeeded(
                    run,
                );
                return;
            }
            run();
        },

        closeChatPanelCompletely() {
            const app_state_store = useAppState();
            const unlinkedPanelState = useUnlinkedPanelState();
            const alignStore = useAlignStore();
            const chatStore = useChat();
            const utilities = Utilities();
            const state = this.active?.state ?? {};
            app_state_store.toggleInviteDrawer(false, state, false);
            app_state_store.toggleNewDirectoryPanel(false);
            this.toggleProductivityPanel(false);
            app_state_store.worklineFilterDrawer = false;
            chatStore.closeEmbeddedWorkerSettings();

            if (this.is_get_help_now_opened && utilities.chatPopover.value) {
                this.toggleGetHelpNowMenuValue(false);
            }

            utilities.toggleChatPopover(
                null,
                true,
                Boolean(unlinkedPanelState.savedRoutes.chat?.name),
            );

            unlinkedPanelState.switchToLastDetachChatRouteFromUnlinkedPanel();
            app_state_store.toggleInviteDrawer(false, state);

            if (
                this.active?.isAgentConversation() &&
                alignStore.selectedStep?.type === MEET_YOUR_KAAMFU_AI
            ) {
                alignStore.setSelectedStep(null);
            }
        },

        isPreviousInHistory() {
            return this.history_index > 0;
        },

        isNextInHistory() {
            const length = this.history.length;
            return this.history_index < length - 1;
        },
        async inviteUsers(
            profile_ids,
            assigned_ids,
            active_conv = this.active,
            item = null,
        ) {
            this.is_invitation_in_progress = true;

            let currently_assigned_id = assigned_ids?.assigned_old;
            let new_assigned_id = assigned_ids?.assigned_new;

            if (currently_assigned_id === undefined) {
                currently_assigned_id = useTeam().currently_assigned;
            }

            if (new_assigned_id === undefined) {
                new_assigned_id = useTeam().assigned;
            }

            if (!item) item = this.getItem(active_conv);

            // if (this.isActiveConversationFake())
            //     return await this.createConversationPlaceholder(
            //         profile_ids,
            //         active_conv,
            //         currently_assigned_id,
            //         new_assigned_id,
            //         item,
            //     );

            let result;

            if (this.active?.is_group || this.active?.is_group_one_off) {
                result = await this.inviteAndAssignUsers(
                    profile_ids,
                    active_conv,
                    currently_assigned_id,
                    new_assigned_id,
                    item,
                );
            } else if (active_conv?.type) {
                result = await this.inviteAndAssignUsersWithRole(
                    profile_ids,
                    active_conv,
                    currently_assigned_id,
                    new_assigned_id,
                    item,
                );
            }
            this.is_invitation_in_progress = false;
            this.updateConversationHistory();
            useAppState().addItemInSelectionHistory();
            return result;
        },

        getItem(active_conv) {
            const item_id = active_conv.originalData?.item?.id;
            if (!item_id) {
                return undefined;
            }
            return useWorkspaceTree().getItemById(item_id);
        },

        async createConversationPlaceholder(
            profile_ids,
            active_conv,
            currently_assigned_id,
            new_assigned_id,
            item = null,
            count = 0,
        ) {
            const payload = {
                type: active_conv.type ?? "item",
                type_id: active_conv.type_id,
                name: active_conv.name,
                user_profile_ids: profile_ids,
                assigned_user_profile_id: new_assigned_id,
            };

            try {
                const {data} = await axios.post(
                    "/conversations/placeholder",
                    payload,
                );

                this.toggleProductivityPanel(false);
                active_conv?.setConversationVariables(data);
                //active_conv.sendInvitationForProfiles(profile_ids);

                // active_conv.sendAssignedMessage(
                //     new_assigned_id,
                //     currently_assigned_id,
                // );

                active_conv.originalData.item.version = data?.item.version;
                useWorkspace().setLastUpdatedItem(data?.item);
                //active_conv?.setConversationVariables(data)
                //active_conv.setAssignedUser(new_assigned_id)
                return data;
            } catch (e) {
                if (count) {
                    Utilities().showErrorSnackbar(
                        "Operation failed. Please refresh and try again.",
                    );
                    return;
                }
                // wait for 2 second
                await new Promise((resolve) => setTimeout(resolve, 2000));
                this.createConversationPlaceholder(
                    profile_ids,
                    active_conv,
                    currently_assigned_id,
                    new_assigned_id,
                    item,
                    1,
                );
            }
        },

        async inviteAndAssignUsers(
            profile_ids,
            active_conv,
            currently_assigned_id,
            new_assigned_id,
            item = null,
            count = 0,
        ) {
            let response,
                message_indexes = [];

            const item_category = active_conv.getItemCategory();
            const new_profile_ids =
                active_conv?.filterNewlyInvitedProfilesFromList(profile_ids);

            if (!profile_ids.length) {
                return;
            }

            try {
                const {data} = await axios.post(
                    `/conversations/${active_conv.id}/invite`,
                    {
                        invited_user_profile_ids: profile_ids,
                        assigned_user_profile_id:
                            item_category?.automatic_end_assignment
                                ? currently_assigned_id
                                : new_assigned_id,
                    },
                );

                const conversation_data = data;
                if (!conversation_data.participants) {
                    return;
                }

                //response = await Promise.all([
                //active_conv.sendInvitationForProfiles(new_profile_ids),
                /* active_conv.sendAssignedMessage(
                     new_assigned_id,
                     currently_assigned_id,
                 ),*/
                //]);

                //message_indexes.push(response[0]);
                //message_indexes.push(response[1]?.[0]);
                //message_indexes.push(response[1]?.[1]);
                //message_indexes.push(response[2]);

                active_conv?.updateParticipants(conversation_data.participants);
                if (active_conv?.is_item) {
                    this.syncItemInWorkspaceTree({
                        version: {
                            user_profile_id_assignee:
                                data?.item?.version?.user_profile_id_assignee ??
                                null,
                            user_profile_id_creator:
                                data?.item?.version?.user_profile_id_creator ??
                                useUserStore().profile?.id,
                            current_assignment_status:
                                data?.item?.version
                                    ?.current_assignment_status ?? "assigned",
                        },
                        id: data?.item?.id,
                        item_category_id: data?.item?.item_category_id,
                    });
                    useWorkspace().setLastUpdatedItem(data?.item);
                    active_conv.originalData.item.version = data?.item?.version;
                }

                // Add logic for item group conversations
                if (active_conv?.is_item_group) {
                    // Update the chin data for item groups
                    const {handleInviteApi} = await import("../Helpers/chin");
                    handleInviteApi(
                        {
                            selected_entities: [
                                {
                                    id: active_conv.type_id,
                                    entity_type: active_conv.type,
                                },
                            ],
                            update_store:
                            useWorkspace().setChinItemGroupProfileIds,
                        },
                        true,
                    );
                }

                //active_conv.setAssignedUser(new_assigned_id)
                return data;
            } catch (e) {
                if (count) {
                    console.log("Failed to Invite");
                    Utilities().showErrorSnackbar(
                        "Operation failed. Please refresh and try again.",
                    );
                    message_indexes.forEach(
                        (index) =>
                            index &&
                            active_conv.updateMessageStateAsFailedByIndex(
                                index,
                            ),
                    );
                    return;
                }

                // wait for 2 second
                //removed recursive call.
                /*     await new Promise((resolve) => setTimeout(resolve, 2000));
                     this.inviteAndAssignUsers(
                         profile_ids,
                         active_conv,
                         currently_assigned_id,
                         new_assigned_id,
                         item,
                         1,
                     );*/
            }
        },

        async inviteAndAssignUsersWithRole(
            profile_ids,
            active_conv,
            currently_assigned_id,
            new_assigned_id,
            item = null,
            count = 0,
        ) {
            let response,
                message_indexes = [];

            const item_category = active_conv.getItemCategory();

            const {current_participants, new_modified_participants} =
                useInvitation();

            const new_profile_ids = new_modified_participants?.filter(
                (profile_id) => {
                    return !current_participants.includes(profile_id);
                },
            );

            const { invitedProfiles: profiles_with_role, tempIds: temp_ids } =
                buildWorkPanelInviteProfilesPayload(
                    useInvitation().user_profiles_with_roles,
                );

            try {
                const resource_type =
                    useInvitation().RESOURCE_TYPES[active_conv?.type];
                const assignment_changed = useTeam().is_assigned_id_changed;

                const payload = {
                    invited_user_profile_ids: filterWorkPanelInviteProfileIds(
                        profile_ids,
                        temp_ids,
                    ),
                    invited_profiles: profiles_with_role,
                    assigned_user_profile_id:
                        item_category?.automatic_end_assignment
                            ? currently_assigned_id
                            : new_assigned_id,
                    space_only_removed_profile_ids: [
                        ...useInvitation().space_only_removed_ids,
                    ],
                };

                if (payload.assigned_user_profile_id === undefined) {
                    payload.assigned_user_profile_id = null;
                }

                if (
                    assignment_changed &&
                    payload.assigned_user_profile_id != null
                ) {
                    const assigner_profile_id = useTeam().assigner;
                    if (assigner_profile_id != null) {
                        payload.assigned_on_behalf_of = assigner_profile_id;
                    }
                }

                const is_new_conversation = !active_conv.id;
                if (is_new_conversation) {
                    active_conv.isRequestToCreateTwilioConversationAlreadyMade = true;
                    const temporary_id = this.createTemporaryConversationId();
                    payload.temporary_id = temporary_id;
                    active_conv.setTemporaryId(temporary_id);
                    const pendingMediaIds =
                        this.getPendingUploadedMediaIdsForConversation(
                            active_conv,
                        );
                    if (pendingMediaIds.length) {
                        payload.uploaded_media_ids = pendingMediaIds;
                    }
                }

                this.loading = true;
                const {data} = await axios.post(
                    `/invites/${resource_type}/${active_conv.type_id}`,
                    payload,
                );

                const result = data.data;
                const conversation_data = result.conversation;

                if (!conversation_data) {
                    return;
                }

                if (payload.uploaded_media_ids?.length) {
                    this.clearPendingUploadedMediaIdsForConversation(
                        active_conv,
                        payload.uploaded_media_ids,
                    );
                }

                const item_version = conversation_data?.item?.version;

                console.log("syncItem", {
                    user_profile_id_assignee: assignment_changed
                        ? payload.assigned_user_profile_id
                        : item_version?.user_profile_id_assignee,
                    user_profile_id_creator: assignment_changed
                        ? (payload.assigned_on_behalf_of ??
                            useUserStore().profile?.id)
                        : item_version?.user_profile_id_creator,
                    current_assignment_status:
                        item_version?.current_assignment_status ?? "assigned",
                });

                this.syncItemInWorkspaceTree({
                    version: {
                        user_profile_id_assignee: assignment_changed
                            ? payload.assigned_user_profile_id
                            : item_version?.user_profile_id_assignee,
                        user_profile_id_creator: assignment_changed
                            ? (payload.assigned_on_behalf_of ??
                                useUserStore().profile?.id)
                            : item_version?.user_profile_id_creator,
                        current_assignment_status:
                            item_version?.current_assignment_status ??
                            "assigned",
                    },
                    id: conversation_data?.item?.id,
                    item_category_id: conversation_data?.item?.item_category_id,
                });

                active_conv?.updateParticipants(conversation_data.participants);

                if (active_conv?.is_item) {
                    useWorkspace().setLastUpdatedItem(result.conversation.item);
                    active_conv.originalData = result.conversation;
                    handleInviteApi(
                        {
                            selected_entities: [
                                {
                                    id: active_conv.type_id,
                                    entity_type: active_conv.type,
                                },
                            ],
                            update_store: useWorkspace().setChinItemProfileIds,
                        },
                        true,
                    );
                }

                if (active_conv?.is_item_group) {
                    const {handleInviteApi} = await import("../Helpers/chin");
                    handleInviteApi(
                        {
                            selected_entities: [
                                {
                                    id: active_conv.type_id,
                                    entity_type: active_conv.type,
                                },
                            ],
                            update_store:
                            useWorkspace().setChinItemGroupProfileIds,
                        },
                        true,
                    );
                }

                active_conv.forceUiUpdate();

                if (
                    is_new_conversation ||
                    new_profile_ids.includes(
                        useUserStore().getCurrentUserProfileId(),
                    )
                ) {
                    active_conv.restoreConversationData(conversation_data);
                }

                active_conv?.setTemporaryId(null);
                //active_conv.setAssignedUser(new_assigned_id)
                return data;
            } catch (e) {
                console.trace(e);
                const api_message = e.response?.data?.message;
                const has_api_message =
                    typeof api_message === "string" && api_message.length > 0;

                if (count) {
                    console.log("Failed to Invite");
                    Utilities().showErrorSnackbar(
                        has_api_message
                            ? api_message
                            : "Operation failed. Please refresh and try again.",
                    );
                    message_indexes.forEach(
                        (index) =>
                            index &&
                            active_conv.updateMessageStateAsFailedByIndex(
                                index,
                            ),
                    );
                    return;
                }

                if (has_api_message) {
                    Utilities().showErrorSnackbar(api_message);
                    this.setLoader();
                    return;
                }

                // wait for 2 second
                await new Promise((resolve) => setTimeout(resolve, 2000));
                // this.inviteAndAssignUsers(
                //     profile_ids,
                //     active_conv,
                //     currently_assigned_id,
                //     new_assigned_id,
                //     item,
                //     1
                // );
            } finally {
                this.setLoader();
            }
        },

        async createConversationUserToBoard(user_profile_ids) {
            if (this.isActiveConversationFake()) {
                const data = {
                    type: this.active.type ?? "board",
                    type_id: this.active.type_id,
                    name: this.active.name,
                    user_profile_ids: user_profile_ids,
                };
                return axios
                    .post("/conversations/placeholder", data)
                    .then(({data}) => {
                        this.active?.setConversationVariables(data);
                        return data;
                    });
            }

            return axios
                .post(`/conversations/${this.active.id}/invite`, {
                    user_profile_ids: user_profile_ids,
                })
                .then(({data}) => {
                    const conversationData = data;
                    if (!conversationData.participants) return;

                    this.active?.setConversationVariables(conversationData);
                    return data;
                });
        },

        async createDMWithProfile(user_profile_id) {
            if (!user_profile_id) return console.log("invalid profile");

            if (
                shouldDismissLocateForConversationType(
                    "dm",
                    useAppState().entity_navigate_with_locator,
                )
            ) {
                useWorkspace().dismissActiveLocateHighlight();
            }

            if (this.dm_profiles_in_progress.includes(user_profile_id)) return;

            this.dm_profiles_in_progress.push(user_profile_id);

            useAuthorityLevelStore().setSelectedProfileId(user_profile_id);
            useChat().setStandaloneAssociateProfileId(user_profile_id);

            if (this.openDmConversationForProfileId(user_profile_id)) {
                this.removeProfileIdFromDmProgressList(user_profile_id);
                Utilities()?.toggleChatPopover(true);
                return;
            }

            try {
                this.startLoader();
                const {data} = await axios.post(`/conversations/dm-invite`, {
                    user_profile_id: user_profile_id,
                });
                const conversation_data = data;
                if (!conversation_data.participants) return;

                this.setHistoryTeamPanel();
                await new Promise((resolve) => setTimeout(resolve, 1000));
                let conversation_index = this.handlers.findIndex(
                    (conv) => conv.id == conversation_data.id,
                );
                if (conversation_index == -1) {
                    const conv_handler = new Conversation(conversation_data);
                    conv_handler.auto_focus = true;
                    this.handlers.unshift(conv_handler);
                    conversation_index = 0;
                }
                this.openConversastionAt(conversation_index);
                useAppState()?.toggleInviteDrawer(false);
                Utilities()?.toggleChatPopover(true);
                this.removeProfileIdFromDmProgressList(user_profile_id);

                return this.handlers.find(
                    (conv) => conv.id == conversation_data.id,
                );
            } catch (error) {
                if (error.response?.status === HttpStatusCodes.NOT_FOUND) {
                    useAppState().setShowSupportAdminDialog();
                }

                console.log("Creating dm failed.", error);
            } finally {
                this.stopLoader();
            }
        },

        async createDMWithKaamfuAgent(agent_profile) {
            const user_profile_id = agent_profile?.id;
            if (!user_profile_id) return;
            if (this.dm_profiles_in_progress.includes(user_profile_id)) return;
            this.dm_profiles_in_progress.push(user_profile_id);
            this.initial_loading = true;

            try {
                const {data} = await axios.post(`/conversations/dm-invite`, {
                    user_profile_id: user_profile_id,
                });

                const conversation_data = data;
                if (data?.align_steps?.length > 0) {
                    data.align_steps.forEach((align_step) => {
                        useAlignStore().markAlignStepAsCompleted(
                            align_step?.id,
                        );
                    });
                }
                if (!conversation_data.participants) return;

                this.setHistoryTeamPanel();
                let conversation_index = this.handlers.findIndex(
                    (conv) => conv.id == conversation_data.id,
                );
                if (conversation_index == -1) {
                    const conv_handler = new Conversation(conversation_data);
                    this.handlers.unshift(conv_handler);
                    conversation_index = 0;
                }
                this.openConversastionAt(conversation_index);
                this.toggleGetHelpNowMenuValue(false);
                useAppState()?.toggleInviteDrawer(false);
                useChat().setMessageTab();
                Utilities()?.toggleChatPopover(true);
                this.removeProfileIdFromDmProgressList(user_profile_id);
                this.is_invitation_in_progress = false;

                return this.handlers.find(
                    (conv) => conv.id == conversation_data.id,
                );
            } catch (e) {
                console.log("Creating dm failed.", e);
                this.removeProfileIdFromDmProgressList(user_profile_id);
            } finally {
                this.initial_loading = false;
            }
        },

        removeProfileIdFromDmProgressList(user_profile_id) {
            this.dm_profiles_in_progress = this.dm_profiles_in_progress.filter(
                (id) => id != user_profile_id,
            );
        },

        async createGroup(
            user_profile_ids,
            name,
            type,
            is_one_off,
            active_conv = this.active,
        ) {
            active_conv.setIsCreatingConversation(true);
            const temporary_id = this.createTemporaryConversationId();
            active_conv.setTemporaryId(temporary_id);
            active_conv.set;
            const data = {
                avatar: useTeam().group_image,
                name: name.trim().replace(/\s+/g, " "),
                type: type,
                user_profile_ids: user_profile_ids,
                is_one_off: is_one_off,
                temporary_id: temporary_id,
            };
            useTeam().group_image = null;

            try {
                const result = await axios.post(
                    `/conversations/create-group`,
                    data,
                );
                const conversation_data = result.data;
                if (!conversation_data.participants) {
                    return;
                }

                this.setHistoryTeamPanel();
                active_conv?.restoreConversationData(conversation_data);
                // active_conv?.sendGroupCreationInvitaitonForProfiles(
                //     user_profile_ids,
                //     is_one_off,
                // );
                active_conv?.forceUiUpdate();
                active_conv.setIsCreatingConversation(false);
                active_conv.setTemporaryId(null);
                this.list.push(conversation_data);
                this.addGroupConversation(active_conv);

                const conversation_index = this.findIndexById(
                    conversation_data.id,
                );
                if (conversation_index >= 0) {
                    this.setActiveConversationAtIndex(conversation_index, true);
                }

                return conversation_data;
            } catch (e) {
                active_conv.setIsCreatingConversation(false);
            }
        },

        getFakeIndex() {
            return this.handlers.findIndex((conv) => conv.isFake());
        },
        createFakeConversastion(convData) {
            const fakeConvIndex = this.getFakeIndex();
            if (fakeConvIndex >= 0) {
                this.active?.setConversationVariables(convData);
                this.setActiveConversationAtIndex(fakeConvIndex);
                return;
            }

            const convHandler = new Conversation(convData, false);
            convHandler.setAsFakeConversation();
            this.handlers.unshift(convHandler);
            this.setActiveConversationAtIndex(0);
        },

        getConversationByParticipantId(id) {
            return this.handlers.filter((conv) =>
                conv.participants.find((user) => user.id === id),
            );
        },

        getConversationBySpaceId(id) {
            return this.handlers.find(
                (item) => item.space_id === id && item.is_space,
            );
        },

        getConversationByBoardId(id) {
            return this.handlers.find(
                (item) => item.board_id === id && item.is_board,
            );
        },

        getConversationByItemId(id) {
            return this.handlers.find((item) => item.itemId === id);
        },

        getConversationByItemCategoryId(id) {
            return this.handlers.find(
                (item) => item.type_id === id && item.is_item_group,
            );
        },
        getConversationByTypeAndTypeId(type, type_id) {
            return this.handlers.find(
                (item) => item.type === type && item.type_id === type_id,
            );
        },

        getConversationByBoardCategoryId(id) {
            return this.handlers.find(
                (item) => item.type_id === id && item.is_board_group,
            );
        },

        getConversationBySpaceCategoryId(id) {
            return this.handlers.find(
                (item) => item.type_id === id && item.is_space_group,
            );
        },

        getConversationsByItemIds(ids) {
            const conversations = new Map();
            if (!Array.isArray(ids) || ids.length === 0) {
                return [];
            }
            let leftToFind = ids.length;

            for (const conv of this.handlers) {
                if (
                    conv.type === useInvitation().ACCESS_LEVELS.ITEM &&
                    ids.some((id) => id == conv.type_id)
                ) {
                    !conversations.has(conv.id) &&
                    conversations.set(conv.id, conv);
                    leftToFind--;
                }
                if (!leftToFind) break;
            }

            return Array.from(conversations.values());
        },

        getConversationById(id) {
            return this.handlers.find((item) => item.id === id);
        },

        getSpaceWithBoards(days = null) {
            const spaces = new Map();
            let space, board;

            this.handlers.forEach((conv) => {
                if (!conv.isItem()) return;

                if (days) {
                    if (!this.compareLastMessageDateToNumberOfDays(conv, days))
                        return;
                }

                const space_id = conv.space_id;
                const board_id = conv.board_id;
                space = spaces.get(space_id);

                if (!space)
                    spaces.set(
                        space_id,
                        (space = {
                            id: space_id,
                            name: conv.space_name,
                            boards: new Map(),
                        }),
                    );

                board = space.boards.get(board_id);

                if (!board)
                    space.boards.set(
                        board_id,
                        (board = {
                            id: board_id,
                            name: conv.board_name,
                            unread_messages_count: 0,
                        }),
                    );

                board.unread_messages_count += conv.unreadMessagesCount ?? 0;
            });

            const final_data = Array.from(spaces.values());
            final_data.forEach((space) => {
                space.boards = Array.from(space.boards.values());
            });

            return final_data;
        },

        compareLastMessageDateToNumberOfDays(conv, days) {
            const date_created =
                conv?.getTwilioConversation()?.lastMessage?.dateCreated;
            let dateDifference = 0;

            if (date_created)
                dateDifference = DateUtilities.getDifferenceInDays(
                    date_created,
                    new Date(),
                );

            if (days >= dateDifference) return true;

            return false;
        },

        getBoardsBySpaceId(space_id) {
            return this.handlers.filter(
                (convo, index) =>
                    this.handlers.findIndex(
                        (item) =>
                            item.board &&
                            item.board?.id === convo.board?.id &&
                            item.space?.id === space_id,
                    ) === index,
            );
        },

        getAllUnreadCountByBoardId(board_id, space_id) {
            let total_unread = 0;

            this.handlers.forEach((item) => {
                if (
                    item.board &&
                    item.space &&
                    item.board.id === board_id &&
                    item.space.id === space_id
                )
                    total_unread += item.unreadMessagesCount;
            });

            return total_unread;
        },
        setDocumentByConversationId(conv_id) {
            const conv = this.findById(conv_id);

            if (!conv?.id) return;

            if (!conv?.document) conv.document = {};

            const doc_type = conv.is_item ? conv.type : "conversation";
            const doc_id = conv.is_item ? conv.type_id : conv.id;

            const conv_document = {
                id: conv.document.id ?? null,
                documentable_type: doc_type,
                documentable_id: doc_id,
                content: conv.document.content ?? "",
                title: conv.document.title ?? conv.name,
            };
            conv.document = conv_document;
            return conv;
        },

        prepareDocuementForConversation(conv_id) {
            const conv = this.findById(conv_id);

            if (!conv?.id) return;

            let doc_type = "conversation",
                doc_id = conv.id;

            if (
                !(
                    conv.is_dm ||
                    conv.is_group ||
                    conv.is_group_one_off ||
                    conv.is_topic
                )
            ) {
                doc_type = conv.type;
                doc_id = conv.type_id;
            }

            return {
                id: null,
                documentable_type: doc_type,
                documentable_id: doc_id,
                content: "",
                title: conv.name,
            };
        },

        updateConnectionStatusIfNotConnected() {
            const {isTwilioConnected} = TwilioClientObserver();
            if (isTwilioConnected()) return;
            // setTimeout(() => this.disconnected(), 2000);
        },

        setIsEndAssignment(val) {
            this.is_end_assignment = val;
        },

        syncUnreadIndicator(conversation_id, values = {}) {
            if (!conversation_id) {
                return null;
            }

            const indicator = mergeUnreadIndicator(
                this.unread_indicators[conversation_id],
                values,
            );
            this.unread_indicators[conversation_id] = indicator;

            return indicator;
        },

        getUnreadIndicator(conversation_id) {
            return this.unread_indicators[conversation_id] ?? null;
        },

        getUnreadSeparatorIndex(conversation_id) {
            return (
                this.unread_indicators[conversation_id]
                    ?.separator_after_index ?? null
            );
        },

        clearUnreadSeparator(conversation_id) {
            const indicator = this.unread_indicators[conversation_id];
            if (!indicator || indicator.separator_after_index == null) {
                return;
            }

            // window.logger?.("[Unread:separator-cleared]", {
            //     conversation_id,
            //     separator_after_index: indicator.separator_after_index,
            //     unread_since_last_visit: indicator.unread_since_last_visit,
            // });

            this.unread_indicators[conversation_id] = {
                ...indicator,
                separator_after_index: null,
                unread_since_last_visit: 0,
            };
        },

        /**
         * Claim the chat scroll for a message before its conversation is even
         * loaded, so the unread line jump does not steal a "view original".
         */
        reserveMessageScroll({
            conversation_sid = null,
            conversation_id = null,
            message_sid = null,
            reason = null,
        }) {
            if (!message_sid || !reason) {
                return;
            }

            this.message_scroll_reservation = {
                conversation_sid,
                conversation_id,
                message_sid,
                reason,
                reserved_at: Date.now(),
            };
        },

        releaseMessageScroll(message_sid = null) {
            if (
                message_sid &&
                this.message_scroll_reservation?.message_sid !== message_sid
            ) {
                return;
            }

            this.message_scroll_reservation = null;
        },

        hasHigherPriorityScrollReservation(conversation, reason) {
            const reservation = this.message_scroll_reservation;
            if (!reservation) {
                return false;
            }

            if (
                Date.now() - reservation.reserved_at >
                MESSAGE_SCROLL_RESERVATION_TTL_MS
            ) {
                this.message_scroll_reservation = null;
                return false;
            }

            const is_same_conversation =
                (reservation.conversation_sid &&
                    reservation.conversation_sid === conversation?.sid) ||
                (reservation.conversation_id != null &&
                    String(reservation.conversation_id) ===
                        String(conversation?.id));

            if (!is_same_conversation) {
                return false;
            }

            return (
                getMessageScrollPriority(reservation.reason) >
                getMessageScrollPriority(reason)
            );
        },

        addUnreadConversation(unread_messages_count, data) {
            const index = this.unread_conversations.findIndex(
                (item) => item?.id === data.id,
            );

            const conversation = this.findById(data.id);
            const current_profile_id = useUserStore().getCurrentUserProfileId();

            if (!conversation) {
                // Still allow remove when the object is gone from memory.
                if (!unread_messages_count && index >= 0) {
                    this.unread_conversations.splice(index, 1);
                    if (this.unread_conversations.length === 0) {
                        document.title = `Kaamfu`;
                    } else {
                        document.title = `(${this.unread_conversations.length}) Kaamfu`;
                    }
                }
                return;
            }

            const sender_user_profile_id =
                conversation.getMessageAuthor({author: data?.author})?.id ??
                null;

            // Only skip *adding* when an incoming/self-sent message is from the
            // current user. Removals (count 0) and manual marks without author
            // must still update the queue.
            if (
                unread_messages_count &&
                sender_user_profile_id != null &&
                sender_user_profile_id == current_profile_id
            ) {
                return;
            }

            let log_type = null;
            if (unread_messages_count) {
                const is_aq_message =
                    conversation.isUnReadMessageContainsAQMessage(
                        unread_messages_count,
                    );

                if (!is_aq_message) {
                    return;
                }

                const latest_unread_messages =
                    conversation.getUnreadMessages(unread_messages_count);
                const has_task_start_resume_recipient_aq =
                    latest_unread_messages.some((message) => {
                        return (
                            isTaskStartOrResumeSubType(
                                message?.data?.sub_type,
                            ) &&
                            conversation.messageCountsAsActivityQueue(message)
                        );
                    });

                if (
                    !has_task_start_resume_recipient_aq &&
                    useUserStore().hasOnlySubordinateTimerAQEvents(
                        current_profile_id,
                    )
                ) {
                    const subordinate_ids =
                        useTeamPanel().subordinates.find(
                            (subordinate) =>
                                subordinate.assigner_id == current_profile_id,
                        )?.assignees ?? [];

                    if (
                        !conversation.isUnReadMessageFromImmediateSubordinates(
                            unread_messages_count,
                            subordinate_ids,
                        )
                    ) {
                        return;
                    }
                }

                if (index >= 0) {
                    const existing = this.unread_conversations[index];
                    this.unread_conversations.splice(index, 1, {
                        ...data,
                        queued_at: existing?.queued_at ?? Date.now(),
                    });
                } else {
                    this.unread_conversations.push({
                        ...data,
                        queued_at: Date.now(),
                    });
                }
                log_type = "add";
            } else if (index >= 0) {
                this.unread_conversations.splice(index, 1);
                log_type = "remove";
            }

            if (this.unread_conversations.length === 0)
                document.title = `Kaamfu`;
            else
                document.title = `(${this.unread_conversations.length}) Kaamfu`;

            if (log_type) {
                axios.post(`/conversations/${data.id}/log`, {
                    type: log_type,
                    sender_id: data?.author,
                    sender_user_profile_id: sender_user_profile_id,
                    queue_count: this.unread_conversations.length,
                    message_type: data.type,
                });
            }
        },
        setKaamfuRequestToConversation(conv_data, attr) {
            const req = Array.isArray(attr.kaamfu_request)
                ? undefined
                : [attr.kaamfu_request];

            const old_attention_request = useRequest().urgent_request?.find(
                (req) => req.id === attr.kaamfu_request.id,
            );

            if (old_attention_request) {
                return;
            }

            useRequest().setUrgentRequest(
                useRequest().urgent_request.concat(req),
            );
        },
        async loadConversationKaamfuRequests() {
            try {
                const {data} = await axios.get(
                    "/conversations/load-kaamfu-requests",
                );
                useRequest().setUrgentRequest(data?.data ?? []);
            } catch (e) {
                console.error("failed to load attention requests.");
            }
        },

        async initialize(conversations, is_reinitialize = false) {
            try {
                this.list = conversations;
                const new_conversation_ids = this.list.map(
                    (conversation) => conversation.id,
                );
                const old_conversation_ids = this.handlers.map(
                    (conversation) => conversation.state.id,
                );
                const conversations_to_add = new_conversation_ids.filter(
                    (conversation_id) =>
                        !old_conversation_ids.includes(conversation_id),
                );
                const conversations_to_delete = old_conversation_ids.filter(
                    (conversation_id) =>
                        !new_conversation_ids.includes(conversation_id),
                );

                for (const conversation_id of conversations_to_delete) {
                    let conversation_index = this.handlers.findIndex(
                        (conversation) =>
                            conversation.state.id === conversation_id,
                    );

                    const restore_state =
                        this.buffered_restore_conversation?.find(
                            (state) => state.id === conversation_id,
                        );

                    if (restore_state) {
                        const current_conversation = this.handlers.find(
                            (conversation) =>
                                conversation.state.id === conversation_id,
                        );

                        if (current_conversation) {
                            await current_conversation.restoreConversation(
                                restore_state,
                            );
                        }
                    } else if (conversation_index !== -1) {
                        this.handlers.splice(conversation_index, 1);
                        console.log(
                            "remove activity at index",
                            conversation_index,
                        );
                    }

                    // if (this.buffered_restore_conversation?.length > 0) {
                    //     console.log(current_conversation.state.id, current_conversation.state.sid)
                    //     console.log(this.buffered_restore_conversation[0].id, this.buffered_restore_conversation[0].sid)
                    //     debugger;
                    //     const restore_state = this.buffered_restore_conversation.find(state =>
                    //         state.id === current_conversation.state.id
                    //     )

                    //     if (restore_state) {
                    //         current_conversation.restoreConversation(restore_state)
                    //         restore_state.unsend_messages.forEach(message => {
                    //             current_conversation.processToSendConversation(
                    //                 message.body,
                    //                 message.attributes,
                    //                 message,
                    //                 (messageIndex) => {
                    //                     if (messageIndex > -1) message.index = messageIndex;

                    //                     message.is_proceesing = false;

                    //                     current_conversation.updateLastReadUsersForMessages();
                    //                 },
                    //                 () => {
                    //                     message.is_proceesing = true;
                    //                     state.messages[message.temp_index].is_proceesing = true;
                    //                     state.messages[message.temp_index].temp_index = message.temp_index;
                    //                 }
                    //             );
                    //         })
                    //     }
                    // }
                }

                //let index = 0;
                //let limit = 30;
                for (const conversation_id of conversations_to_add) {
                    //index++;
                    let conversation = this.list.find(
                        (list) => list.id === conversation_id,
                    );
                    if (conversation) {
                        if (is_reinitialize) {
                            this.handlers.splice(
                                0,
                                0,
                                new Conversation(conversation),
                            );
                        } else {
                            this.handlers.push(
                                new Conversation({
                                    ...conversation,
                                    is_paginated: true,
                                }),
                            );
                        }
                    }

                    /* if (index % limit === 0) {
                        await new Promise((resolve) =>
                            setTimeout(resolve, 3000),
                        );
                    } */
                }

                if (is_reinitialize) {
                    this.handlers.forEach((current_conversation) => {
                        const new_conversation = this.list.find(
                            (list) => list.id === current_conversation.state.id,
                        );
                        if (new_conversation) {
                            current_conversation.setConversationVariables(
                                new_conversation,
                            );
                            current_conversation.fetchTwilioParticipants();
                        }
                    });
                }

                if (!is_reinitialize) {
                    this.loadGroups();
                    this.loadFlaggedConversations();
                }
            } catch (e) {
                console.error("reInitializeConversation failed", e);
            }

            this.is_initialized = true;
            if (conversations.length === 0) {
                useAppState().conv_loader = false;
            }
        },
        async archiveConversationById(conversation_id) {
            const conversation = this.findById(conversation_id);
            try {
                this.loading = true;
                this.isArchivingOrRestoring = true;
                await axios.post(`conversations/${conversation_id}/archive`);
                conversation.setArchiveStatus(true);
                if (conversation) {
                    conversation.setAsArchivedConversation();
                    conversation.setTwilioConversation(null);
                    conversation.resetHandlersEvent();
                    conversation.removeAllParticipants();
                }
            } catch (e) {
                conversation.setArchiveStatus(false);
                console.log(
                    "failed to archive conversation " + conversation_id,
                );
            } finally {
                this.setLoader();
                this.isArchivingOrRestoring = false;
            }
        },

        async loadConversationBySID(sid, open_conversation = false) {
            try {
                if (this.non_org_conversations_ids.includes(sid)) {
                    return;
                }

                const conversation = this.findBySId(sid);

                if (conversation) {
                    if (open_conversation) {
                        await this.openOrLoadConversationById(conversation.id);
                    }
                    return;
                }

                //console.trace("loadConversationBySID", sid);

                if (this.conversations_sids_in_loading.has(sid)) {
                    if (open_conversation) {
                        this.conversations_sids_in_loading.set(
                            sid,
                            open_conversation,
                        );
                    }
                    return;
                }

                this.conversations_sids_in_loading.set(sid, open_conversation);

                const {data} = await axios.get(`/conversations/${sid}/sid`);
                const conv_handler = new Conversation(data, true);

                this.handlers.unshift(conv_handler);

                if (this.conversations_sids_in_loading.get(sid)) {
                    await this.openOrLoadConversationById(data.id);
                } else {
                    this.activeIndex += 1;
                }

                this.removeConversationSidFromLoading(sid);

                if (conv_handler.is_group) {
                    this.addGroupConversation(conv_handler);
                }
            } catch (error) {
                if (error?.response?.status === HttpStatusCodes.FORBIDDEN) {
                    this.non_org_conversations_ids.push(sid);
                    this.non_org_conversation_aq.set(
                        error.response.data.organization_id,
                        true,
                    );
                }

                console.log(
                    "error while fetching conversation by SID:",
                    error.response,
                );
                this.removeConversationSidFromLoading(sid);
                throw error;
            } finally {
                // this.setLoader();
            }
        },

        removeConversationSidFromLoading(sid) {
            this.conversations_sids_in_loading.delete(sid);
        },

        removeConversationIdFromLoading(id) {
            this.conversations_ids_in_loading.delete(id);
        },

        async loadConversationById(
            id,
            history_update = true,
            messageToJump = null,
        ) {
            try {
                if (this.conversations_ids_in_loading.has(id)) {
                    return;
                }
                this.conversations_ids_in_loading.set(id, true);
                const {data} = await axios.get(`/conversations/find/${id}`);
                if (!data) {
                    console.error("conversation not found with id", id);
                    return;
                }

                const convData = data.data ?? data;
                const type = convData.conversationable_type ?? convData.type;
                const name = convData.name ?? "";

                const payloadId =
                    convData.conversationable_id ??
                    convData.type_id ??
                    convData.id;

                await this.openConversationOfType(
                    type,
                    {id: payloadId, name},
                    history_update,
                    id,
                    messageToJump,
                );
            } catch (error) {
                if (error.response?.status === HttpStatusCodes.NOT_FOUND) {
                    useAppState().setShowSupportAdminDialog();
                }

                console.log(
                    "error while fetching conversation by ID:",
                    error.response,
                );
                throw error;
            } finally {
                this.removeConversationIdFromLoading(id);
            }
        },

        async openOrLoadConversationById(
            id,
            with_history = true,
            messageToJump = null,
        ) {
            if (!id) return console.log("Invalid conversation");
            let conversation_index = this.findIndexById(id);
            if (conversation_index >= 0) {
                const conv = this.handlers[conversation_index];
                const conversationType = conv.type ?? conv.state?.type;
                if (
                    shouldDismissLocateForConversationType(
                        conversationType,
                        useAppState().entity_navigate_with_locator,
                    )
                ) {
                    useWorkspace().dismissActiveLocateHighlight();
                }
                this.setActiveConversationAtIndex(
                    conversation_index,
                    with_history,
                );
                this.stopLoader();

                if (messageToJump) {
                    if (conv.hasTwilioConversationSet()) {
                        conv.jumpToMessage(
                            messageToJump.id,
                            messageToJump.index,
                        );
                    } else {
                        conv.setPendingMessageToJump(messageToJump);
                    }
                }

                useChat().setMessageTab();
                conv?.markAsReadIfCorrectWindowOpenForMessageRead();
                return;
            }

            this.loading = true;
            await this.loadConversationById(id, with_history, messageToJump);
        },

        async getConnectedConversation(id) {
            const {data} = await axios.get(
                `/conversations/quick-access/${id}`,
            );

            return data.data;
        },

        async loadGroups() {
            if (this.group_conversations.length) return;

            const {data} = await axios.get("/conversations?type=group");
            let group_conversations = data.conversations ?? [];

            group_conversations = group_conversations.sort((a, b) =>
                a.name.localeCompare(b.name),
            );

            this.group_conversations = [];
            let conv_handler = null;
            group_conversations.forEach((convData) => {
                conv_handler = this.findById(convData.id);
                if (!conv_handler)
                    conv_handler = new Conversation(convData, false);

                this.group_conversations.push(conv_handler);
            });
        },

        addGroupConversation(conversation) {
            const has_conversation = this.group_conversations.find(
                (conv) => conv.id === conversation.id,
            );

            if (has_conversation) {
                return;
            }

            this.group_conversations.push(conversation);
            this.group_conversations = this.group_conversations.sort((a, b) =>
                a.name.localeCompare(b.name),
            );
        },

        openArchivedGroupConversation(conv_handler) {
            this.loading = true;
            try {
                this.handlers.unshift(conv_handler);
                conv_handler.loadConversation();
                this.setActiveConversationAtIndex(0);
            } finally {
                this.setLoader();
            }
        },

        prependGroupIfNotLoaded(conv_handler) {
            this.loading = true;
            let conv_index_in_active_handlers = this.findIndexById(
                conv_handler.id,
            );

            if (conv_index_in_active_handlers >= 0) {
                this.setLoader();
                return;
            }

            this.handlers.unshift(conv_handler);
            conv_handler.initialize();
            this.setLoader(false, 2000);
        },
        openGroupConversation(group) {
            this.openOrLoadConversationById(group.id);
        },
        setLoader(val = false, time_out_seconds = 300) {
            setTimeout(() => {
                this.loading = val;
            }, time_out_seconds);
        },
        startLoader() {
            this.loading = true;
        },
        stopLoader() {
            this.loading = false;
        },

        setPendingMembershipForFakeItem(fakeItemId, supportToggle = false) {
            this.pending_membership_fake_item_id = fakeItemId;
            this.pending_membership_support_toggle = supportToggle;
        },

        matchesPendingMembershipFakeItem(fakeItemId) {
            return this.pending_membership_fake_item_id === fakeItemId;
        },

        clearPendingMembershipForFakeItem() {
            this.pending_membership_fake_item_id = null;
            this.pending_membership_support_toggle = false;
        },

        setTemporaryTitle(title = null) {
            this.title = title;
        },

        async loadFlaggedConversations() {
            const {data} = await axios.get("/flagged-conversations");

            let flagged_conversations = data.data ?? [];

            this.flagged_conversations = [];

            let conv_handler = null;

            flagged_conversations.forEach((convData) => {
                conv_handler = this.findById(convData.id);
                if (!conv_handler)
                    conv_handler = new Conversation(convData, false);

                this.flagged_conversations.push(conv_handler);
            });
        },

        async toggleFlagConversation() {
            try {
                const active_conversation = this.active;

                if (!active_conversation?.id) {
                    Utilities().showInfoSnackbar(
                        "Conversation could not be flagged because the conversation hasn't started yet.",
                    );

                    return;
                }

                // if(!active_conversation
                //     ?.getActiveParticipantIds()
                //     ?.includes(useUserStore().getCurrentUserProfile()?.id)){
                //     Utilities().showInfoSnackbar(
                //         "Conversation could not be flagged because You are not part of this conversation.",
                //     );
                //
                //     return;
                // }

                const {data} = await axios.post(
                    `/conversations/${active_conversation.id}/toggle-flag`,
                );

                const conversation_id = data?.data?.id;
                const is_flagged = data?.data?.is_flagged;

                const index = this.handlers.findIndex(
                    (c) => c.id === conversation_id,
                );
                const flaggedIndex = this.flagged_conversations.findIndex(
                    (c) => c.id === conversation_id,
                );

                if (index !== -1) {
                    this.handlers[index].state.is_flagged = is_flagged;
                    active_conversation.state.is_flagged = is_flagged;

                    if (is_flagged) {
                        if (flaggedIndex === -1) {
                            this.flagged_conversations.push(
                                this.handlers[index],
                            );
                        }
                    } else {
                        if (flaggedIndex !== -1) {
                            this.flagged_conversations.splice(flaggedIndex, 1);
                        }
                    }
                } else {
                    const groupIndex = this.group_conversations.findIndex(
                        (c) => c.id === conversation_id,
                    );
                    if (groupIndex !== -1) {
                        this.group_conversations[groupIndex].state.is_flagged =
                            is_flagged;
                        active_conversation.state.is_flagged = is_flagged;

                        if (is_flagged) {
                            if (flaggedIndex === -1) {
                                this.group_conversations.push(
                                    this.group_conversations[groupIndex],
                                );
                            }
                        } else {
                            if (flaggedIndex !== -1) {
                                this.group_conversations.splice(groupIndex, 1);
                            }
                        }
                    }
                }

                Utilities().showSuccessSnackbar(
                    is_flagged
                        ? "Conversation flagged successfully"
                        : "Conversation unflagged successfully",
                );

                return data.data;
            } catch (e) {
                console.log(e);

                Utilities().showErrorSnackbar(
                    "Operation failed. Please refresh and try again.",
                );
            }
        },

        async checkMembershipAccess(
            conversation_id = this?.active?.type_id,
            resource_type = null,
        ) {
            try {
                this.hasConversationAccess = false;
                const permission_type = "invite";

                if (!resource_type) {
                    resource_type =
                        useInvitation().RESOURCE_TYPES[this?.active?.type];
                }

                const response = await axios.get(
                    `/work-panel/${resource_type}/${conversation_id}/permissions/${permission_type}`,
                );

                if (response.status === 200) {
                    this.hasConversationAccess = response?.data?.data;
                }
            } catch (error) {
                const status = error.response?.status;

                if (status === HttpStatusCodes.UNPROCESSABLE_ENTITY) {
                    Utilities().showErrorSnackbar(
                        "This conversation is no longer available.",
                    );
                } else if (!error.response || error.code === "ECONNABORTED") {
                    console.warn(
                        "Temporary network issue. Skipping error snackbar.",
                    );
                    return;
                } else {
                    Utilities().showErrorSnackbar(
                        "Failed to retrieve membership access",
                    );
                }

                console.log(error);
            }
        },

        createTemporaryConversationId() {
            return crypto.randomUUID();
        },

        removeSupportAdminDMConversation(support_admin_profile_id) {
            if (!support_admin_profile_id) {
                return false;
            }

            const conversation = this.handlers.find((conversation) => {
                return (
                    conversation.isDm() &&
                    conversation.participants.findIndex(
                        (participant) =>
                            participant?.id == support_admin_profile_id,
                    ) > -1
                );
            });

            if (conversation && conversation?.id) {
                this.removeConversationsFromHistory([conversation.id]);
                this.removeAndUpdateActiveIndexById(conversation.id);
            }
        },

        updateConversationParticipant(user_profile) {
            if (!user_profile) {
                return false;
            }
            const is_self_update =
                user_profile.id == useUserStore().getCurrentUserProfileId();
            this.handlers.forEach((conversation) => {
                const participant = conversation.participants.find(
                    (participant) => participant?.id == user_profile.id,
                );
                if (participant) {
                    participant.name = user_profile.display_name;
                    participant.avatar_url = user_profile.avatar_url;
                    participant.full_name = user_profile.full_name;
                }

                // Update conversation name for DM conversations when other participant's name changes
                if (participant && conversation.isDm() && !is_self_update) {
                    conversation.name = user_profile.display_name;
                }
            });
        },

        async fetchOtherOrganizationAq() {
            const {data} = await axios.get("conversations/aq/all");
            if (Array.isArray(data.data)) {
                data.data.forEach((item) => {
                    this.non_org_conversation_aq.set(
                        item.organization_id,
                        !!item.aq,
                    );
                });
            }
        },

        initializeTheArchivedConversations() {
            const archived_conversations = this.handlers.filter(
                (conversation) => conversation.is_archived,
            );

            if (archived_conversations.length === 0) return;

            archived_conversations.forEach(async (conversation) => {
                let result = null;
                try {
                    result = await axios.post("/conversations/verify", {
                        id: conversation.id,
                        name: conversation.name,
                        type: conversation.type,
                        type_id: conversation.type_id,
                    });
                } catch (error) {
                    if (error.response) {
                        if (
                            error?.response?.status ===
                            HttpStatusCodes.FORBIDDEN
                        ) {
                            Utilities().showErrorSnackbar(
                                error?.response?.data?.message,
                            );
                            return;
                        } else if (
                            error?.response?.status ===
                            HttpStatusCodes.INTERNAL_SERVER_ERROR
                        ) {
                            Utilities().showErrorSnackbar(
                                "Server error occurred.",
                            );
                            return;
                        }

                        const errors = error.response.data?.errors;
                        if (errors?.archived && errors?.messages?.length) {
                            const conv_handler = new Conversation(
                                errors?.conversation,
                                true,
                            );

                            const index = this.handlers.findIndex(
                                (conversation) =>
                                    conversation.id === conv_handler.id,
                            );

                            if (index !== -1) {
                                this.handlers.splice(index, 1, conv_handler);
                                conv_handler.restoreMessages(errors?.messages);
                                conv_handler.setAsArchivedConversation();
                            }

                            return;
                        }
                    }
                }
            });
        },

        toggleGetHelpNowMenu() {
            if (
                this.is_get_help_now_opened &&
                !Utilities()?.chatPopover.value
            ) {
                Utilities()?.toggleChatPopover(true);
            } else {
                this.toggleGetHelpNowMenuValue();
                Utilities()?.toggleChatPopover(true);
                this.toggleTeamPanel(false);
                this.toggleProductivityPanel(false);
            }
        },

        toggleGetHelpNowMenuValue(val = null) {
            if (val === null) {
                this.is_get_help_now_opened = !this.is_get_help_now_opened;
            } else {
                this.is_get_help_now_opened = val;
            }
        },
    },
});
